import React, { useEffect, useMemo, useState } from 'react';
import { NewHackathonDialog, useOrganizedHackathons } from '../Hackathon/NewHackathonDialog';
import { useAuth } from '../../services/useAuth';
import { login, logout } from '../../services/auth.service';
import './AppHeader.scss';
import { Logo } from '../logo/icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { atom } from 'jotai';
import { Link, useNavigate } from 'react-router-dom';
import { useDesktopMode } from '../../services/hooks/useDesktopMode';
import { useHackathon } from '../../hooks/useHackathon';
import { useMyOrganizations } from '../../hooks/useMyOrganizations';
import { useCurrentHackathonId } from '../Theme/AppThemeProvider';
import { NotificationBell } from '../Notifications/NotificationBell';
import Container from '@mui/material/Container/Container';
import Stack from '@mui/material/Stack/Stack';
import Box from '@mui/material/Box/Box';
import Button from '@mui/material/Button/Button';
import CircularProgress from '@mui/material/CircularProgress/CircularProgress';
import IconButton from '@mui/material/IconButton/IconButton';
import Avatar from '@mui/material/Avatar/Avatar';
import Divider from '@mui/material/Divider/Divider';
import ListItemIcon from '@mui/material/ListItemIcon/ListItemIcon';
import Typography from '@mui/material/Typography/Typography';
import Tooltip from '@mui/material/Tooltip/Tooltip';
import MenuIcon from '@mui/icons-material/Menu';
import Drawer from '@mui/material/Drawer/Drawer';
import List from '@mui/material/List/List';
import ListItemButton from '@mui/material/ListItemButton/ListItemButton';
import ListSubheader from '@mui/material/ListSubheader/ListSubheader';
import { alpha, useTheme } from '@mui/material/styles';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AddIcon from '@mui/icons-material/Add';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BusinessIcon from '@mui/icons-material/Business';
import HomeIcon from '@mui/icons-material/Home';


export const NavMenuOpened = atom(false);


export default function AppHeader() {
    const desktopMode = useDesktopMode();
    const theme = useTheme();

    const { user, loading } = useAuth();
    const { organizations } = useMyOrganizations();
    const adminOrganizations = useMemo(() => organizations.filter(o => o.isAdmin), [organizations]);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const navigate = useNavigate();
    // New hackathon: from scratch, or duplicate one you've run. The dialog opens at once and the list loads inside it
    // (fresh each time, not on every page); with nothing to duplicate, it gives way to the empty form
    const [newOpen, setNewOpen] = useState(false);
    const { organized, loading: organizedLoading } = useOrganizedHackathons(newOpen);
    useEffect(() => {
        if (newOpen && !organizedLoading && organized?.length === 0) {
            setNewOpen(false);
            navigate('/new');
        }
    }, [newOpen, organizedLoading, organized, navigate]);
    const startNew = () => setNewOpen(true);

    const [drawerOpen, setDrawerOpen] = useState(false);

    const toggleDrawer = (open: boolean) => {
        setDrawerOpen(open);
    }

    const hackathonId = useCurrentHackathonId();

    const { hackathon } = useHackathon(hackathonId);

    const isOrganizer = useMemo(() => {
        if (!user || !hackathon) {
            return false;
        }

        return hackathon.organizers.some(o => o.email === user.email);
    }, [user, hackathon])

    const isJudge = useMemo(() => {
        if (!user || !hackathon) {
            return false;
        }

        return hackathon.judges.some(o => o.email === user.email);
    }, [user, hackathon])

    return (
        <Box
            component="header"
            className="app-header no-print-no-display"
            id="back-to-top-anchor"
            sx={{
                position: 'sticky',
                top: 0,
                zIndex: theme.zIndex.appBar,
                // a branded event washes the bar with its colour (no stripe along the edge)
                background: theme.brand.isCustom && hackathonId
                    ? `linear-gradient(${alpha(theme.brand.rawPrimary, 0.1)}, ${alpha(theme.brand.rawPrimary, 0.1)}), ${alpha(theme.palette.background.default, 0.9)}`
                    : alpha(theme.palette.background.default, 0.86),
                backdropFilter: 'saturate(180%) blur(8px)',
                borderBottom: `1px solid ${theme.palette.divider}`
            }}>
            <Container>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minHeight: 64 }}>
                    {
                        hackathon && hackathon.ideas.length > 2 && <IconButton title={'Menu'} aria-label="Open ideas menu" edge="start" onClick={() => {
                            toggleDrawer(true)
                        }}><MenuIcon /></IconButton>
                    }
                    {/* always ours: a branded hackathon shows its logo and name in its banner, and its color in the stripe above */}
                    <Link to="/" className='main-link'>
                        <Logo size="small" />
                        {desktopMode && <Typography variant="h6" component="span" color="text.primary">Hack in a Box</Typography>}
                    </Link>
                    <Box sx={{ flex: 1 }} />
                    {!loading && !!user && <NotificationBell />}
                    <Tooltip title="Help">
                        <IconButton onClick={() => {
                            window.open('/how/to', '_blank');
                        }}>
                            <HelpOutlineIcon />
                        </IconButton>
                    </Tooltip>
                    {
                        // signed out, there's nothing to duplicate: straight to the form, which keeps a draft until they sign in
                        desktopMode && !loading && (
                            <Button variant={'outlined'} startIcon={<AddIcon />} onClick={user ? startNew : () => navigate('/new')}>New hackathon</Button>
                        )
                    }
                    {
                        loading && <CircularProgress size={24} />
                    }
                    {
                        !loading && !user && <Button variant="contained" onClick={() => { login() }}>Log in</Button>
                    }
                    {
                        (!loading && !!user) && <>
                            <IconButton id="basic-button"
                                aria-label="Account menu"
                                aria-controls={open ? 'basic-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={open ? 'true' : undefined}
                                onClick={handleClick}>
                                <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: '0.95rem' }}>
                                    {(user.username || user.email || '?').charAt(0).toUpperCase()}
                                </Avatar>
                            </IconButton>
                            <Menu
                                id="basic-menu"
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleClose}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                slotProps={{ paper: { sx: { minWidth: 220, mt: 1 } } }}
                                MenuListProps={{
                                    'aria-labelledby': 'basic-button',
                                }}
                            >
                                <Box sx={{ px: 2, py: 1 }}>
                                    <Typography variant="subtitle2">{user.username}</Typography>
                                    <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                                </Box>
                                <Divider />
                                <MenuItem onClick={() => { handleClose(); navigate('/'); }}>
                                    <ListItemIcon><HomeIcon fontSize="small" /></ListItemIcon>
                                    My hackathons
                                </MenuItem>
                                {
                                    !desktopMode && <MenuItem onClick={() => { handleClose(); startNew(); }}>
                                        <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
                                        New hackathon
                                    </MenuItem>
                                }
                                {
                                    adminOrganizations.map(org => (
                                        <MenuItem key={org._id} onClick={() => { handleClose(); navigate(`/org/${org.slug}`); }}>
                                            <ListItemIcon><BusinessIcon fontSize="small" /></ListItemIcon>
                                            {org.name} settings
                                        </MenuItem>
                                    ))
                                }
                                {
                                    // a company is where branding and the people who run events live; offer it until they have one
                                    adminOrganizations.length === 0 && <MenuItem onClick={() => { handleClose(); navigate('/org/new'); }}>
                                        <ListItemIcon><BusinessIcon fontSize="small" /></ListItemIcon>
                                        Set up your company
                                    </MenuItem>
                                }
                                {user.isAdmin && <MenuItem onClick={() => { handleClose(); navigate('/admin'); }}>
                                    <ListItemIcon><AdminPanelSettingsIcon fontSize="small" /></ListItemIcon>
                                    Admin
                                </MenuItem>}
                                <Divider />
                                <MenuItem onClick={logout}>
                                    <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                                    Log out
                                </MenuItem>
                            </Menu>
                        </>
                    }
                </Stack>
            </Container>
            <Drawer open={drawerOpen} onClose={() => {
                toggleDrawer(false)
            }}>
                <List sx={{ width: 320 }}>
                    {
                        hackathon && <ListItemButton onClick={() => {
                            navigate(`/h/${hackathon.slug}`);
                            toggleDrawer(false);
                        }}><Typography variant="subtitle1">{hackathon.name}</Typography></ListItemButton>
                    }
                    {
                        hackathon && (isOrganizer || isJudge) && <ListItemButton onClick={() => {
                            navigate(`/h/${hackathon.slug}/judge`);
                            toggleDrawer(false);
                        }}>Judging</ListItemButton>
                    }
                    <ListSubheader>Ideas</ListSubheader>

                    {
                        hackathon && hackathon.ideas.map(idea => {
                            return <ListItemButton sx={{ pl: 4 }} key={idea._id} onClick={() => {
                                navigate(`/h/${hackathon.slug}/idea/${idea._id}`);
                                toggleDrawer(false);
                            }}>{idea.title}</ListItemButton>
                        })
                    }

                </List>
            </Drawer>
            <NewHackathonDialog open={newOpen} onClose={() => setNewOpen(false)} hackathons={organized || []} loading={organizedLoading} />
        </Box>
    )
}
